// Header role switcher. Renders only when the user has more than one
// app-renderable role context. Switching calls /auth/role-switch and
// refreshes /auth/me, which re-dispatches the shell.

import { useT } from '@fajrarabi/i18n/react';
import { useState } from 'react';

import { useAuth } from '~/lib/auth';
import { appRoleContexts, roleLabelKey } from '~/lib/roles';

export function RoleSwitcher() {
  const t = useT();
  const { currentRoleContext, availableRoleContexts, setRole } = useAuth();
  const [busy, setBusy] = useState(false);

  const contexts = appRoleContexts(availableRoleContexts);
  const first = contexts[0];
  if (contexts.length <= 1 || !first) return null;

  const active =
    currentRoleContext && contexts.includes(currentRoleContext) ? currentRoleContext : first;

  async function handleChange(next: string) {
    if (next === active) return;
    setBusy(true);
    try {
      await setRole(next);
    } finally {
      setBusy(false);
    }
  }

  return (
    <label className="inline-flex items-center">
      <span className="sr-only">{t('app.common.role_switcher_label')}</span>
      <select
        value={active}
        disabled={busy}
        onChange={(e) => void handleChange(e.target.value)}
        aria-label={t('app.common.role_switcher_label')}
        className="rounded-full bg-cream-100 px-3 py-1.5 text-sm font-semibold text-burgundy-500 ring-1 ring-burgundy-500/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-burgundy-500 disabled:opacity-50"
      >
        {contexts.map((ctx) => (
          <option key={ctx} value={ctx}>
            {t(roleLabelKey(ctx))}
          </option>
        ))}
      </select>
    </label>
  );
}
